import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormRoot, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { formatSupplierApiError } from "../api/suppliers-client";
import { useCreateSupplier } from "../api/suppliers-queries";
import { SUPPLIER_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";
import {
  SupplierFormCard,
  type SupplierFormValues,
} from "../ui/SupplierFormCard";

const FormSchema = z.object({
  name: z.string().trim().min(1, "ຕ້ອງໃສ່ຊື່ຜູ້ສະໜອງ"),
  code: z.string().trim(),
  contactName: z.string().trim(),
  phone: z.string().trim(),
  email: z.string().trim(),
  address: z.string().trim(),
  taxId: z.string().trim(),
  paymentTerms: z.string().trim(),
  notes: z.string().trim(),
  isActive: z.boolean(),
});

function emptyToNull(s: string) {
  return s.trim() ? s.trim() : null;
}

export function SupplierCreatePage() {
  const nav = useNavigate({ from: "/app/inventory/suppliers/create" });
  const createSupplier = useCreateSupplier();

  const form = RHF.useForm<SupplierFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      code: "",
      contactName: "",
      phone: "",
      email: "",
      address: "",
      taxId: "",
      paymentTerms: "",
      notes: "",
      isActive: true,
    },
  });

  const onSave = form.handleSubmit(async (values) => {
    try {
      await createSupplier.mutateAsync({
        name: values.name,
        code: values.code.trim() ? values.code.trim().toUpperCase() : null,
        contactName: emptyToNull(values.contactName),
        phone: emptyToNull(values.phone),
        email: emptyToNull(values.email),
        address: emptyToNull(values.address),
        taxId: emptyToNull(values.taxId),
        paymentTerms: emptyToNull(values.paymentTerms),
        notes: emptyToNull(values.notes),
        isActive: values.isActive,
      });
      toast.success("ສ້າງຜູ້ສະໜອງສຳເລັດ");
      nav({ to: "/app/inventory/suppliers" });
    } catch (e: unknown) {
      toast.error(formatSupplierApiError(e));
    }
  });

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: SUPPLIER_MENU_TITLE, to: "/app/inventory/suppliers" },
            { label: "ເພີ່ມຜູ້ສະໜອງ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ເພີ່ມຜູ້ສະໜອງ</h2>
            <p className="text-muted-foreground">{SUPPLIER_MENU_TITLE}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/suppliers" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <FormRoot methods={form} onSubmit={onSave} className="space-y-6">
          <SupplierFormCard form={form} />
        </FormRoot>

        <div className="mt-6 flex flex-wrap justify-between gap-4 border-t pt-6">
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/suppliers" })}
          >
            ຍົກເລີກ
          </Button>
          <Button onClick={onSave} isLoading={createSupplier.isPending}>
            ບັນທຶກ
          </Button>
        </div>
      </Main>
    </>
  );
}
